import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { answerBodyMeasurementIntakeFormQuestion } from '@api';
import { useApplicationStore } from '@data';
import {
  cn,
  Field,
  Input,
  Label,
  ErrorMessage,
  Paragraph,
} from '@quickmd-llc/design-system';
import { BodyMeasurementQuestion } from '@qmd-types/intakeForms';
import {
  FormValues,
  getDefaultFormValues,
  getTotalHeightInches,
  buildSchema,
} from './form';
import { SubmitButton } from '../SubmitButton';
import { useIntakeForm } from '../../../context/useIntakeForm';

type Props = { question: BodyMeasurementQuestion };

export function BodyMeasurementForm({ question }: Props) {
  const patientProfile = useApplicationStore(
    ({ patient }) => patient.patientProfile,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleNextStep, intakeForm, updateAnswer } = useIntakeForm();
  const schema = buildSchema(question.isRequired);
  const { handleSubmit, formState, register } = useForm({
    resolver: zodResolver(schema),
    defaultValues: getDefaultFormValues(question, patientProfile),
  });
  const { errors } = formState;

  const errorWeightMessage = errors?.weightPounds?.message;
  const errorHeightFeetMessage = errors?.heightFeet?.message;
  const errorHeightInchesMessage = errors?.heightInches?.message;
  const hasHeightError = Boolean(
    errorHeightFeetMessage || errorHeightInchesMessage,
  );

  async function onSubmit(data: FormValues) {
    const { intakeFormQuestionId } = question;
    const { intakeFormId } = intakeForm;

    setIsLoading(true);

    const formattedData = {
      heightInches: getTotalHeightInches(data) ?? null,
      weightPounds: data.weightPounds ?? null,
    };

    try {
      await answerBodyMeasurementIntakeFormQuestion({
        intakeFormQuestionId,
        intakeFormId,
        data: formattedData,
      });

      const updatedQuestion = { ...question, ...formattedData };

      updateAnswer(intakeFormQuestionId, updatedQuestion);
      handleNextStep();
    } catch {
      setIsLoading(false);
    }
  }

  return (
    <form
      className="flex flex-1 flex-col gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Field>
        <div className="flex gap-2">
          <Field className="flex-1" error={hasHeightError}>
            <Label>How tall are you?</Label>

            {hasHeightError && (
              <ErrorMessage>
                {errorHeightFeetMessage || errorHeightInchesMessage}
              </ErrorMessage>
            )}

            <Input
              {...register('heightFeet')}
              autoFocus
              step={1}
              pattern={'/^[0-9]+$/'}
              placeholder="Height"
              type="number"
              disableNativeNumericControls
              rightSideLabel={
                <Paragraph
                  className={cn(
                    hasHeightError ? 'text-text-error' : 'text-shade-70',
                  )}
                >
                  ft.
                </Paragraph>
              }
            />
          </Field>

          <Field className="flex-1" error={hasHeightError}>
            <Label>&nbsp;</Label>

            {hasHeightError && <ErrorMessage>&nbsp;</ErrorMessage>}

            <Input
              {...register('heightInches')}
              step={1}
              pattern={'/^[0-9]+$/'}
              placeholder="Height"
              type="number"
              disableNativeNumericControls
              rightSideLabel={
                <Paragraph
                  className={cn(
                    hasHeightError ? 'text-text-error' : 'text-shade-70',
                  )}
                >
                  in.
                </Paragraph>
              }
            />
          </Field>
        </div>
      </Field>

      <Field error={Boolean(errorWeightMessage)}>
        <Label>What is your current weight? (lbs)</Label>

        {errorWeightMessage && (
          <ErrorMessage>{errorWeightMessage}</ErrorMessage>
        )}

        <Input
          {...register('weightPounds')}
          step={1}
          pattern={'/^[0-9]+$/'}
          placeholder="Current weight"
          type="number"
          disableNativeNumericControls
          rightSideLabel={
            <Paragraph
              className={cn(
                errorWeightMessage ? 'text-text-error' : 'text-shade-70',
              )}
            >
              lbs.
            </Paragraph>
          }
        />
      </Field>

      <SubmitButton isLoading={isLoading} />
    </form>
  );
}
